import React, { useContext, useRef, useState } from "react"
import LinkResolver from "../../utils/linkResolver"
import { Link } from "../atoms/Link"
import { DownloadCount } from "./DownloadCount"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import breakpoint from "styled-components-breakpoint"
import { DownloadModal } from "./DownloadModal"
import { useOnClickOutside } from "@karbonhq/marketing-shared-utils/hooks/useOnClickOutside"
import { GatsbyImage } from "gatsby-plugin-image"
import { ButtonAnchor, ButtonResetCSS } from "../atoms/Buttons"
import theme from "../../settings/theme"
import chevron from "../../images/chevron-down-small-white.svg"
import { useAddtoKarbon } from "../hooks/useAddtoKarbon.tsx"
import { Lines } from "../atoms/Spinners"
import { mr } from "styled-components-spacing"
import { VisitorLocaleContext } from "../providers/VisitorLocaleProvider"
import { AddToKarbonAdminNoticeModal } from "./AddToKarbonAdminNoticeModal"
import { AddToKarbonErrorModal } from "./AddToKarbonErrorModal"
import { graphql, useStaticQuery } from "gatsby"
import { AddToKarbonSignInModal } from "./AddToKarbonSignInModal"

const regionMap = {
  Australia: {
    shortName: "AUS",
  },
  Canada: { shortName: "CAN" },
  France: { shortName: "FR" },
  India: { shortName: "IND" },
  Ireland: { shortName: "IRL" },
  "New Zealand": {
    shortName: "NZ",
  },
  "South Africa": {
    shortName: "ZA",
  },
  "United Kingdom": {
    shortName: "UK",
    toolTip: "This template is for The United Kingdom only",
  },
  "United States": {
    shortName: "USA",
    toolTip: "This template is for The United States only",
  },
}

const RegionTag = styled(({ region, className }) => {
  const tooltip = (
    <div className={"tooltip"}>
      <div className={"inner"}>
        {regionMap[region]
          ? `${
              regionMap[region].tooltip || `This template is for ${region} only`
            } `
          : `This template can be used in any country`}
      </div>
    </div>
  )
  return (
    <div className={className}>
      {regionMap[region] ? regionMap[region].shortName : "Global"}
      {tooltip}
    </div>
  )
})`
  font-size: 12px;
  color: ${props => props.theme.colors.darkGrey};
  background: ${props => props.theme.colors.ash};
  border-radius: 40px;
  display: inline-block;
  padding: 0 8px;
  position: relative;
  text-transform: uppercase;
  top: -1px;

  .tooltip {
    text-transform: none;
    position: absolute;
    top: 0;
    left: 50%;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.4s ease-out 0.3s;
  }
  :hover .tooltip {
    visibility: visible;
    opacity: 1;
  }

  .inner {
    position: absolute;
    top: 0;
    left: 0;
    background: rgb(0 0 0/80%);
    width: 140px;
    border-radius: 2px;
    color: white;
    font-size: 13px;
    padding: 2px 8px;
    line-height: 1.1;
    transform: translate(-70px, calc(-100% - 2px));
    text-align: center;
  }
`
export const Icon = styled(GatsbyImage)`
  border: 1px solid ${props => props.theme.colors.ash};
  border-radius: 6px;
  width: 40px !important;
  height: 40px !important;
`

export const SlatEl = styled.article`
  color: black;
  border: solid ${props => props.theme.colors.lightGrey};
  border-width: ${props => (props.first ? `1px` : `0`)} 0 1px 0;
  padding: 16px 0;
  ${props =>
    !props.hideStats &&
    css`
      min-height: 95px;
    `}
  display: grid;
  grid-template-columns: 40px 1fr;
  grid-column-gap: 16px;

  ${breakpoint("lg")(
    () => css`
      padding: 16px;
      border-width: ${props => (props.first ? `1px` : `0`)} 1px 1px 1px;
      grid-template-columns: 40px 1fr 200px;
    `
  )}

  ${Icon} {
    margin: 4px 16px 0 0;
  }

  .main {
    min-width: 200px;
    margin-right: 16px;
    flex: 1;
  }

  .title {
    font-size: 20px;
    margin: 2px 8px 0 0;
    line-height: 1.2;
    font-weight: normal;
    display: inline;

    ${breakpoint(`sm`)(css`
      font-size: 18px;
    `)}
  }

  .quiet {
    font-size: 16px;
    color: ${props => props.theme.colors.baseGrey};
    margin: 0;
    height: 21px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions {
    justify-content: flex-end;
    min-width: 200px;
    display: none;
    ${breakpoint("lg")(
      () => css`
        display: flex;
      `
    )}
  }
`

const Menu = styled.div`
  position: relative;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.2);
  background: white;
  border-radius: 4px;
  text-align: left;
  z-index: 1;
  display: ${props => (props.open ? `block` : `none`)};
`
/**
 * @param {string} text - text to show in toggle button
 * @description function to pass to modal that describes the toggle button
 */
const toggle = text =>
  function toggle(show) {
    return (
      <button
        css={css`
          ${ButtonResetCSS};
          display: block;
          padding: 8px 20px;
          cursor: pointer;
        `}
        onClick={() => show()}
      >
        {text}
      </button>
    )
  }

const AddToKarbonOption = ({ template, add, setOpen }) => {
  const { karbonData } = useContext(VisitorLocaleContext)
  const canAddToKarbon =
    template.karbonTemplateIDs?.length && karbonData.isLoggedIn

  // This link can be either:
  // Disabled, open a modal saying you need to talk to your admin
  // Or actually add the thing to karbon
  const Link = props => (
    <button
      data-e2e="button__addToKarbon"
      {...props}
      css={css`
        ${ButtonResetCSS};
        display: block;
        padding: 8px 20px;
        color: ${template.karbonTemplateIDs?.length
          ? `currentColor`
          : theme.colors.baseGrey};
        cursor: ${template.karbonTemplateIDs?.length ? `pointer` : `default`};
      `}
    >
      Add To Karbon
    </button>
  )

  // this template is addable but you're not logged in
  if (template.karbonTemplateIDs?.length && !karbonData.isLoggedIn) {
    return (
      <AddToKarbonSignInModal
        slug={template.slug}
        toggle={show => (
          <Link
            onClick={() => {
              show()
            }}
          />
        )}
      />
    )
  }

  // Yeah this is addable but you're not an admin
  if (canAddToKarbon && !karbonData.data.isAdmin) {
    return (
      <AddToKarbonAdminNoticeModal
        toggle={show => (
          <Link
            onClick={() => {
              show()
            }}
          />
        )}
      />
    )
  }

  // You go for it
  if (canAddToKarbon) {
    return (
      <>
        <Link
          onClick={() => {
            add()
            setOpen(false)
          }}
        />
        {/* If add throws an error then we'll need to show the error modal */}
      </>
    )
  }

  // Nah bro. Not logged in or no template ID or something unexpected
  return <Link />
}

const ActionMenu = ({ template, setDownloads, downloads }) => {
  const [open, setOpen] = useState()
  const ref = useRef()
  const { add, adding, successURL, error, failedTemplates } = useAddtoKarbon(
    template.karbonTemplateIDs,
    template.slug
  )

  useOnClickOutside(ref, () => setOpen(false))

  const ActionButton = () => {
    if (successURL)
      return (
        <ButtonAnchor
          data-e2e="button__viewTemplate"
          target="_blank"
          href={successURL}
        >
          {template.karbonTemplateIDs?.length === 1 ? "Open" : "View Templates"}
        </ButtonAnchor>
      )

    if (adding)
      return (
        <Lines
          data-e2e="global__spinner"
          css={css`
            ${mr(2)}
          `}
          size={1}
        />
      )

    return (
      <ButtonAnchor
        data-e2e="button__getTemplate-dropdown"
        css={css`
          background: url("${chevron}") ${props => props.theme.colors.coral}
            right 15px top calc(50% + 2px) no-repeat;
          background-size: 10px;
          padding-right: 32px;
        `}
        onClick={toggleOpen}
      >
        Get
      </ButtonAnchor>
    )
  }

  const toggleOpen = () => setOpen(!open)

  return (
    <div
      ref={ref}
      css={`
        position: absolute;
        text-align: right;
      `}
    >
      <ActionButton />
      <Menu open={open}>
        <AddToKarbonOption add={add} template={template} setOpen={setOpen} />
        <DownloadModal
          formUrl={template.formUrl}
          fileUrl={template.file.file.url}
          fileName={template.title}
          contentfulId={template.contentfulId}
          incrementDownloads={() => setDownloads(downloads + 1)}
          linkUrl={LinkResolver(template.internal.type, template.slug)}
          toggle={toggle("Download")}
        />
      </Menu>
      <AddToKarbonErrorModal error={error} failedTemplates={failedTemplates} />
    </div>
  )
}

export const Slat = ({ template, first, hideStats }) => {
  const [downloads, setDownloads] = useState(template.downloads || 0)
  const linkUrl = LinkResolver(template.internal.type, template.slug)
  const { defaultIcon } = useStaticQuery(
    graphql`
      query {
        defaultIcon: contentfulAsset(title: { eq: "template-default-icon" }) {
          gatsbyImageData(layout: FIXED, height: 38, width: 38)
        }
      }
    `
  )

  let icon = null

  if (template.icon) {
    // if the template has an icon, we give this preference
    icon = template.icon.gatsbyImageData
  } else if (template.createdBy?.some(creator => creator.featuredImage)) {
    let i = 0
    // otherwise, we go to the creator and keep searching until we get an icon from the creator array
    while (!icon && i < template.createdBy.length) {
      icon = template.createdBy[i].featuredImage?.gatsbyImageData
      i++
    }
  } else {
    // if we still don't have an icon, let it be the default
    icon = defaultIcon.gatsbyImageData
  }

  return (
    <SlatEl
      first={first}
      id={template.contentfulId}
      hideStats={hideStats}
      data-e2e={"container__slat"}
    >
      <Link to={linkUrl} tabindex="-1">
        <Icon image={icon} alt={`${template.title} icon`} />
      </Link>
      <div className={"main"}>
        <Link className={"link"} to={linkUrl}>
          <h1 className={"title"}>{template.title}</h1>
          <RegionTag region={template.region} />
          <div className="quietWrapper">
            <div className="quiet">{template.description.description}</div>
          </div>
          {!hideStats && (
            <div
              css={css`
                display: flex;
              `}
            >
              {/* <StarChart
              staticRatings={template.ratings}
              contentfulId={template.contentfulId}
            /> */}
              <DownloadCount
                downloads={downloads}
                contentfulId={template.contentfulId}
              />
            </div>
          )}
        </Link>
      </div>
      <div className={"actions"}>
        <ActionMenu
          template={template}
          downloads={downloads}
          setDownloads={setDownloads}
        />
      </div>
    </SlatEl>
  )
}

Slat.propTypes = {
  hideStats: PropTypes.bool,
}
